/**
 * @ Author: Housefolios
 * @ Create Time: 2021-12-16 11:36:56
 * @ Modified by: David Helmick
 * @ Modified time: 2025-12-04 18:19:51
 * @ Description: Summary page used for properties that are not owned
 */

import React, { useState, useEffect, useRef } from 'react'
import {
  strategyAbsBottomLeftSx,
  strategyCardGraySx,
  strategyCarouselCardSx,
  strategyFont16Sx,
  strategyFontSemiboldSx,
  strategyFullWidthSx,
  strategyHeight500Sx,
  strategyIconGap5Style,
  strategyIconMl10Style,
  strategyIconPr5Sx,
  strategyLabel12BoldSx,
  strategyLabel16BoldSx,
  strategyLinkBlueSx,
  strategyMargin10Sx,
  strategyMarginBottom10Sx,
  strategyMarginLeft5Sx,
  strategyMaxWidth720Style,
  strategyMutedMetaSx,
  strategyNowrap13Sx,
  strategyPad0Sx,
  strategyPad10Sx,
  strategyPadVert1RemSx,
  strategyPagePadSx,
  strategyPrimaryBadgeSx,
  strategySectionRowLooseSx,
  strategySectionRowSx,
  strategySemiboldMb10Sx,
  strategySummaryInsetPadSx,
  strategyTinyAvatarGreenSx,
  strategyTinyAvatarRedSx,
  strategyTinyAvatarSx,
  strategyTransparentCenterSx,
  strategyWhiteSmallIconSx,
  strategyWhiteTextSx,
  strategyWidthFullRadius0Sx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
// Centralized lodash imports - much cleaner!
import {
  map,
  findIndex,
  isEmpty,
  isUndefined,
  get,
  find,
  filter,
  sum,
  isNil,
  includes,
  concat,
  compact,
  padStart,
  truncate,
  head,
} from '@/utils/lodash'

import {
  Grid,
  Avatar,
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Tooltip,
  Typography,
  IconButton,
  Badge,
  Backdrop,
  CircularProgress,
  useMediaQuery,
  Box,
} from '@mui/material'

import Slider from 'react-slick'

import withSession from '@/housefolios-components/Session/withSession'
import GaugeChart from '@/utils/SafeGaugeChart'
import FormattedDescription from '@/housefolios-components/shared/FormattedDescription'

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import RemoveIcon from '@mui/icons-material/Remove'
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'
import ImageIcon from '@mui/icons-material/Image'
import RoomIcon from '@mui/icons-material/Room'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'

import Map from '../../../Properties/Map'
import { useMutation, useLazyQuery } from '@apollo/client/react'
import {
  CALL_HOUSE_CANARY_PROPERTY_EXPLORER,
  UPDATE_PROPERTY,
} from '@/housefolios-components/PropertyContainer/mutations'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'
import { Home, Image } from '@mui/icons-material'
import { DEFAULT_ANALYTICS } from '@/housefolios-components/Property/fragments'
import EditStrategies from '@/housefolios-components/Dialogs/EditStrategies'
import { getStrategyName } from '@/utils/stratagies'
import CardSummary from '@/housefolios-components/PropertyContainer/CenterPage/summary/summaryCard'
import SkipTraceDisplay from '@/housefolios-components/Common/SkipTrace'
import { mediaViewTabButtonSx } from '@/theme/skinSx'

const flattenObject = (obj, prefix = '', result = {}) => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key
      const value = obj[key]

      if (value === null || value === undefined) {
        result[newKey] = ''
      } else if (Array.isArray(value)) {
        // Handle arrays by creating indexed columns
        if (value.length === 0) {
          result[newKey] = ''
        } else {
          value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              flattenObject(item, `${newKey}.${index}`, result)
            } else {
              result[`${newKey}.${index}`] = item
            }
          })
        }
      } else if (typeof value === 'object') {
        flattenObject(value, newKey, result)
      } else {
        result[newKey] = value
      }
    }
  }
  return result
}

function CenterPageSummary(props) {
  const { property } = props
  const { acquisition = {} } = property
  const { analytics = DEFAULT_ANALYTICS, analysis = {} } = acquisition

  const activeOrg = property?.portfolio?.organization
  const sessionOrg = props.session?.me?.activeOrg

  const hiddenDown = useMediaQuery((theme) =>
    theme.breakpoints.down('sm'),
  )
  const hiddenUp = useMediaQuery((theme) =>
    theme.breakpoints.up('sm'),
  )

  const [activeTab, setActiveTab] = useState('Images')

  const [loading, setLoading] = useState(false)
  const [callPropertyExplorerReport] = useMutation(
    CALL_HOUSE_CANARY_PROPERTY_EXPLORER,
  )

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const percentageFormat = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
  })

  const gaugeFormat = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
  })

  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const propertyDetails1 = [
    {
      name: 'Beds',
      value: sum(property.beds),
    },
    {
      name: 'Baths',
      value: sum(property.baths),
    },
    {
      name: 'Sqft',
      value: sum(property.sqft),
      formatter: numberFormat,
    },
    {
      name: 'Yr Built',
      value: property.year,
    },
    // {
    //   name: 'Property Type',
    //   value: 'Single Family'
    // },
    // {
    //   name: 'Rental Status',
    //   value: 'Leased',
    // },
    // {
    //   name: 'Parking',
    //   value: '2-car garage'
    // },
    // {
    //   name: 'Cooling',
    //   value: 'Swamp Cooler'
    // },
    // {
    //   name: 'Heating',
    //   value: 'Forced air'
    // },
  ]

  const propertyDetails2 = [
    {
      name: 'Water',
      value: property.water,
    },
    {
      name: 'Sewer',
      value: property.sewer,
    },
    {
      name: 'Market',
      value: property.market,
    },
    {
      name: 'Type',
      value: property.type,
    },
    {
      name: 'Plan',
      value: property.plan,
    },
    {
      name: 'Zone',
      value: property.zone,
    },
    {
      name: 'Parcel ID',
      value: property.parcelID,
    },
  ]

  const propertyDetailsPEP = [
    {
      name: 'APN',
      value: property.apn,
    },
    {
      name: 'Lien',
      value: property.lien ? 'True' : 'False',
    },
    {
      name: 'Mortgage Balance',
      value: property.mortgageBalance,
      formatter: currencyFormat,
    },
    {
      name: 'Estimated Value',
      value: property.estimatedValue,
      formatter: currencyFormat,
    },
    {
      name: 'Estimated Equity',
      value: property.estimatedEquity,
      formatter: currencyFormat,
    },
    {
      name: 'Owner 1',
      value: get(property, 'ownership.owner1FullName') || '-',
    },
    {
      name: 'Owner 2',
      value: get(property, 'ownership.owner2FullName') || '-',
    },
    {
      name: 'Linked Property Count',
      value: get(property, 'linkedProperties.totalOwned') || '-',
    },
  ]

  const strategyCards = [
    {
      name: 'Buy & Hold',
      label: 'Buy & Hold',
      percentage:
        (analytics?.buyHold?.onTarget +
          analytics?.buyHold?.aboveTarget) /
        (analytics?.buyHold?.belowTarget +
          analytics?.buyHold?.onTarget +
          analytics?.buyHold?.aboveTarget),
      page: 'BuyHold',
      afterRepairValue: analytics?.buyHold?.arvTotalBuyHold,
      analytics: [
        {
          value: analytics?.buyHold?.cashFlow / 12 || '-',
          arrow: analytics?.buyHold?.cashFlowArrow || 0,
          label: 'Net Cashflow',
          type: 'currency',
        },
        {
          value: analytics?.buyHold?.cashOnCashReturn || '-',
          arrow: analytics?.buyHold?.cashOnCashReturnArrow || 0,
          label: 'CoC ROI',
          type: 'percentage',
        },
        {
          value: analytics?.buyHold?.IRR || '-',
          arrow: analytics?.buyHold?.IRRArrow || 0,
          label: 'IRR',
          type: 'percentage',
        },
        {
          value: analytics?.buyHold?.capRate || '-',
          arrow: analytics?.buyHold?.capRateArrow || 0,
          label: 'Yield on Cost',
          type: 'percentage',
        },
      ],
    },
    {
      name: 'Fix & Flip',
      label: 'Flip Analysis',
      percentage:
        (analytics?.fixFlip?.onTargetFlip +
          analytics?.fixFlip?.aboveTargetFlip) /
        (analytics?.fixFlip?.belowTargetFlip +
          analytics?.fixFlip?.onTargetFlip +
          analytics?.fixFlip?.aboveTargetFlip),
      page: 'FixFlip',
      afterRepairValue: analytics?.fixFlip?.arvTotalFixFlip,
      analytics: [
        {
          value: analytics?.fixFlip?.saleProceeds || '-',
          arrow: analytics?.fixFlip?.saleProceedsArrow || 0,
          label: 'Net Profit',
          type: 'currency',
        },
        {
          value: analytics?.fixFlip?.netROIFixFlip || '-',
          arrow: analytics?.fixFlip?.netROIArrow || 0,
          label: 'Net ROI',
          type: 'percentage',
        },
        {
          value: analytics?.fixFlip?.percentageOfARV || '-',
          arrow: analytics?.fixFlip?.percentageOfARVArrow || 0,
          label: 'All-In Cost to ARV',
          type: 'percentage',
        },
      ],
    },
    {
      name: 'Turnkey End Buyer',
      label: 'Turnkey: End Buyer',
      percentage:
        (analytics?.turnkey?.onTargetTurnkey +
          analytics?.turnkey?.aboveTargetTurnkey) /
        (analytics?.turnkey?.belowTargetTurnkey +
          analytics?.turnkey?.onTargetTurnkey +
          analytics?.turnkey?.aboveTargetTurnkey),
      page: 'Turnkey',
      afterRepairValue: analytics?.turnkey?.arvTotalTurnkey,
      analytics: [
        {
          value: analytics?.turnkey?.cashFlowTurnkey / 12 || '-',
          arrow: analytics?.turnkey?.cashFlowTurnkeyArrow || 0,
          label: 'Net Cashflow',
          type: 'currency',
        },
        {
          value: analytics?.turnkey?.cashOnCashReturnTurnkey || '-',
          arrow:
            analytics?.turnkey?.cashOnCashReturnTurnkeyArrow || 0,
          label: 'CoC ROI',
          type: 'percentage',
        },
        {
          value: analytics?.turnkey?.capRateTurnkey || '-',
          arrow: analytics?.turnkey?.capRateTurnkeyArrow || 0,
          label: 'Cap Rate',
          type: 'percentage',
        },
        {
          value: analytics?.turnkey?.IRRTurnkey || '-',
          arrow: analytics?.turnkey?.IRRTurnkeyArrow || 0,
          label: 'IRR',
          type: 'percentage',
        },
      ],
    },
    {
      name: 'BRRRR',
      label: 'BRRRR',
      percentage:
        (analytics?.BRRRR?.onTargetBRRRR +
          analytics?.BRRRR?.aboveTargetBRRRR) /
        (analytics?.BRRRR?.belowTargetBRRRR +
          analytics?.BRRRR?.onTargetBRRRR +
          analytics?.BRRRR?.aboveTargetBRRRR),
      page: 'BRRRR',
      afterRepairValue: analytics?.BRRRR?.arvTotalBRRRR,
      analytics: [
        {
          value: analytics?.BRRRR?.cashFlowBRRRR / 12 || '-',
          arrow: analytics?.BRRRR?.cashFlowBRRRRArrow || 0,
          label: 'Net Cashflow',
          type: 'currency',
        },
        {
          value: analytics?.BRRRR?.cashOnCashReturnBRRRR || '-',
          arrow: analytics?.BRRRR?.cashOnCashReturnBRRRRArrow || 0,
          label: 'CoC ROI',
          type: 'percentage',
        },
        {
          value: analytics?.BRRRR?.IRRBRRRR || '-',
          arrow: analytics?.BRRRR?.IRRBRRRRArrow || 0,
          label: 'IRR',
          type: 'percentage',
        },
        {
          value: analytics?.BRRRR?.capRateBRRRR || '-',
          arrow: analytics?.BRRRR?.capRateBRRRRArrow || 0,
          label: 'Yield on Cost',
          type: 'percentage',
        },
      ],
    },
    {
      name: 'Short Term Rental',
      label: 'Short Term Rental',
      percentage:
        (analytics?.STR?.onTargetSTR +
          analytics?.STR?.aboveTargetSTR) /
        (analytics?.STR?.belowTargetSTR +
          analytics?.STR?.onTargetSTR +
          analytics?.STR?.aboveTargetSTR),
      page: 'ShortTermRental',
      afterRepairValue: analytics?.STR?.arvTotalSTR,
      analytics: [
        {
          value: analytics?.STR?.cashFlowSTR / 12 || '-',
          arrow: analytics?.STR?.cashFlowSTRArrow || 0,
          label: 'Net Cashflow',
          type: 'currency',
        },
        {
          value: analytics?.STR?.cashOnCashReturnSTR || '-',
          arrow: analytics?.STR?.cashOnCashReturnSTRArrow || 0,
          label: 'CoC ROI',
          type: 'percentage',
        },
        {
          value: analytics?.STR?.IRRSTR || '-',
          arrow: analytics?.STR?.IRRSTRArrow || 0,
          label: 'IRR',
          type: 'percentage',
        },
        {
          value: analytics?.STR?.capRateSTR || '-',
          arrow: analytics?.STR?.capRateSTRArrow || 0,
          label: 'Yield on Cost',
          type: 'percentage',
        },
      ],
    },
    {
      name: 'Wholesale',
      label: 'Wholesale',
      percentage:
        (analytics?.wholesale?.onTargetWholesale +
          analytics?.wholesale?.aboveTargetWholesale) /
        (analytics?.wholesale?.belowTargetWholesale +
          analytics?.wholesale?.onTargetWholesale +
          analytics?.wholesale?.aboveTargetWholesale),
      page: 'Wholesale',
      afterRepairValue: analytics?.wholesale?.arvTotalWholesale,
      analytics: [
        {
          value: analytics?.wholesale?.saleProceedsWholesale || '-',
          arrow:
            analytics?.wholesale?.saleProceedsWholesaleArrow || 0,
          label: 'Net Profit',
          type: 'currency',
        },
        {
          value: analytics?.wholesale?.buyerProfitTotal || '-',
          arrow: analytics?.wholesale?.buyerProfitArrow || 0,
          label: 'Buyer Profit',
          type: 'currency',
        },
      ],
    },
    {
      name: 'Build to Rent',
      label: 'Build to Rent',
      percentage:
        (analytics?.B2R?.onTargetB2R +
          analytics?.B2R?.aboveTargetB2R) /
        (analytics?.B2R?.belowTargetB2R +
          analytics?.B2R?.onTargetB2R +
          analytics?.B2R?.aboveTargetB2R),
      page: 'BuildToRent',
      afterRepairValue: analytics?.B2R?.arvTotalB2R,
      analytics: [
        {
          value: analytics?.B2R?.cashFlowB2R / 12 || '-',
          arrow: analytics?.B2R?.cashFlowB2RArrow || 0,
          label: 'Net Cashflow',
          type: 'currency',
        },
        {
          value: analytics?.B2R?.cashOnCashReturnB2R || '-',
          arrow: analytics?.B2R?.cashOnCashReturnB2RArrow || 0,
          label: 'CoC ROI',
          type: 'percentage',
        },
        {
          value: analytics?.B2R?.capRateB2R || '-',
          arrow: analytics?.B2R?.capRateB2RArrow || 0,
          label: 'Cap Rate',
          type: 'percentage',
        },
        {
          value: analytics?.B2R?.IRRB2R || '-',
          arrow: analytics?.B2R?.IRRArrowB2R || 0,
          label: 'IRR',
          type: 'percentage',
        },
      ],
    },
    {
      name: 'BOR',
      label: 'BOR',
      percentage:
        (analytics?.BOR?.onTargetBOR +
          analytics?.BOR?.aboveTargetBOR) /
        (analytics?.BOR?.belowTargetBOR +
          analytics?.BOR?.onTargetBOR +
          analytics?.BOR?.aboveTargetBOR),
      page: 'BOR',
      afterRepairValue: analytics?.BOR?.arvTotalBOR,
      analytics: [
        {
          value: analytics?.BOR?.averageAnnualReturnBOR || '-',
          arrow: analytics?.BOR?.averageAnnualReturnBORArrow || 0,
          label: 'CoC ROI',
          type: 'percentage',
        },
        {
          value: analytics?.BOR?.IRRBOR || '-',
          arrow: analytics?.BOR?.IRRBORArrow || 0,
          label: 'IRR',
          type: 'percentage',
        },
      ],
    },
    {
      name: 'Astroflip',
      label: 'Astroflip',
      percentage:
        (analytics?.astroFlip?.onTargetAstroFlip +
          analytics?.astroFlip?.aboveTargetAstroFlip) /
        (analytics?.astroFlip?.belowTargetAstroFlip +
          analytics?.astroFlip?.onTargetAstroFlip +
          analytics?.astroFlip?.aboveTargetAstroFlip),
      page: 'Astroflip',
      afterRepairValue: analytics?.astroFlip?.arvTotalAstroFlip,
      analytics: [
        {
          value: analytics?.astroFlip?.saleProceedsAstroFlip || '-',
          arrow:
            analytics?.astroFlip?.saleProceedsAstroFlipArrow || 0,
          label: 'Net Profit',
          type: 'currency',
        },
        {
          value:
            analytics?.astroFlip?.buyerProfitTotalAstroFlip || '-',
          arrow: analytics?.astroFlip?.buyerProfitAstroFlipArrow || 0,
          label: 'Buyer Profit',
          type: 'currency',
        },
      ],
    },
  ]

  function SliderArrowNext(props) {
    const { className, onClick } = props
    return (
      <div className={className} onClick={onClick}>
        <FontAwesomeIcon icon={['fas', 'chevron-right']} />
      </div>
    )
  }

  function SliderArrowPrev(props) {
    const { className, onClick } = props
    return (
      <div className={className} onClick={onClick}>
        <FontAwesomeIcon icon={['fas', 'chevron-left']} />
      </div>
    )
  }

  const slider = useRef()
  const widgetsCarousels1B = {
    dots: true,
    fade: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    adaptiveHeight: true,
    nextArrow: <SliderArrowNext />,
    prevArrow: <SliderArrowPrev />,
  }

  // useEffect(() => {
  //   let index = findIndex(
  //     property.images,
  //     image => image.url === property.mainImage,
  //   )
  //   if (slider.current)
  //     slider.current.slickGoTo(index > 0 ? index : 0)
  // }, [property])

  const [updateProperty] = useMutation(UPDATE_PROPERTY)

  // const switchMain = (index) => () => {
  //   updateProperty({
  //     refetchQueries: [
  //       {
  //         query: GET_PROPERTY,
  //         variables: { id: property._id }
  //       }
  //     ],
  //     variables: {
  //       id: property._id,
  //       propertyInput: { mainImage: property.images[index]._id },
  //     },
  //   }).then(data => {
  //     props.handleUpdateMarket()
  //   })
  // }

  const dateCreated = new Date(property.createdAt)
  const dateCreatedString = `${
    dateCreated.getMonth() + 1
  }/${dateCreated.getDate()}/${dateCreated.getFullYear()}`

  const tabSwitch = (tab) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const gorillaRehabDays = isNil(analysis.rehabDays)
    ? analysis.rehabCost / 500 + 55
    : analysis.rehabDays
  const gorillaFee =
    ((analysis.offerPrice === null
      ? analysis.listPrice
      : analysis.offerPrice) +
      analysis.rehabCost) *
    0.0005 *
    gorillaRehabDays
  const gorillaMinFee =
    (analysis.offerPrice === null
      ? analysis.listPrice
      : analysis.offerPrice) > 100000
      ? (analysis.offerPrice === null
          ? analysis.listPrice
          : analysis.offerPrice) * 0.06
      : 6000
  const gorillaFinalFee =
    gorillaFee > gorillaMinFee ? gorillaFee : gorillaMinFee
  const gorillaSplit =
    gorillaRehabDays <= 30
      ? 0.05
      : gorillaRehabDays <= 60
        ? 0.1
        : gorillaRehabDays <= 90
          ? 0.2
          : gorillaRehabDays <= 120
            ? 0.3
            : 0.4

  const plan =
    find(sessionOrg?.plans || [], {
      value: property.plan,
    }) || {}
  const planImages = plan.images || []
  const propertyImages = property.images || []

  return (
    <>
      <Grid container spacing={2} sx={strategyPagePadSx}>
        <Grid size={{ xs: 12, sm: 7, xl: 8 }}>
          <Card variant="outlined" sx={{ height: '100%', minHeight: 280 }}>
            <Box sx={[{
              display: "flex",
              alignItems: "center",
              py: "0.75rem",
              px: "1.25rem",
              mb: 0,
              backgroundColor: "#fff",
              "html[data-color-mode=\"dark\"] &": { backgroundColor: "#1e1e1e !important", color: "rgba(255, 255, 255, 0.87)" },
              padding: "0.75rem 1.25rem",
              marginBottom: 0,
              borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
              "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
              "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
            }, { display: 'flex', alignItems: 'center', justifyContent: 'space-between', }]}>
              <ButtonGroup variant="text">
                <Button
                  onClick={() => {
                    tabSwitch('Images')
                  }}
                  sx={mediaViewTabButtonSx(activeTab === 'Images')}
                >
                  <ImageIcon sx={strategyIconPr5Sx} />
                  Images
                </Button>
                <Button
                  onClick={() => {
                    tabSwitch('Map')
                  }}
                  sx={mediaViewTabButtonSx(activeTab === 'Map')}
                >
                  <RoomIcon sx={strategyIconPr5Sx} />
                  Map
                </Button>
                <Button
                  onClick={() => {
                    tabSwitch('Street')
                  }}
                  sx={mediaViewTabButtonSx(activeTab === 'Street')}
                >
                  <DirectionsWalkIcon sx={strategyIconPr5Sx} />
                  Street
                </Button>
              </ButtonGroup>

              {hiddenDown ? null : (
                <>
                  {activeTab === 'Images' && (
                    <div>
                      <Tooltip title="Manage Images">
                        <Button
                          onClick={() => props.toggleCenter('Images')}
                         
                          sx={{
                            height: "44px",
                            width: "44px",
                            lineHeight: "44px",
                            backgroundColor: "rgb(91.7692307692%, 96.4494720965%, 99.838612368%) !important",
                            "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important" },
                            borderRadius: "0.65rem",
                          }}
                        >
                          <FontAwesomeIcon icon={['far', 'image']} sx={[strategyLinkBlueSx, { fontSize: "1.15rem !important" }]}/>
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </>
              )}
            </Box>
            {activeTab === 'Images' && (
              <div>
                <div>
                  <Slider ref={slider} {...widgetsCarousels1B} sx={{ flexShrink: 0, width: "100%" }} style={strategyMaxWidth720Style}>
                    {!isEmpty(
                      compact(concat(propertyImages, planImages)),
                    )
                      ? map(
                          compact(concat(propertyImages, planImages)),
                          (image, index) => (
                            <div key={image._id || image.url || index}>
                              <Card sx={[strategyCarouselCardSx, { boxShadow: 'none' }]}>
                                <Grid
                                  container
                                  direction="column" sx={[strategyHeight500Sx, {
                                    justifyContent: 'center',
                                  }]}>
                                  <Grid>
                                    <Box sx={[strategyTransparentCenterSx, {
                                      position: "relative",
                                      borderTopLeftRadius: "inherit",
                                      borderTopRightRadius: "inherit",
                                      "& .img-wrapper-overlay": {
                                        width: "100%",
                                        height: "100%",
                                        transition: "all 0.2s ease-in-out",
                                        opacity: 0,
                                        visibility: "hidden",
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        boxShadow: "inset 0 0 2.3rem 0.5rem #000",
                                        display: "flex",
                                        alignItems: "center",
                                        alignContent: "center",
                                        color: "#fff",
                                        borderTopLeftRadius: "inherit",
                                        borderTopRightRadius: "inherit",
                                        "@media (max-width: 575.98px)": {
                                          "& + img": { width: "100% !important" },
                                        },
                                        "& .overlay-btn-wrapper": {
                                          position: "relative",
                                          zIndex: 3,
                                          textAlign: "center",
                                          width: "100%",
                                        },
                                        "&::before": {
                                          zIndex: 2,
                                          position: "absolute",
                                          left: 0,
                                          top: 0,
                                          content: "\"\"",
                                          width: "100%",
                                          height: "100%",
                                          transition: "all 0.2s ease-in-out",
                                          background: "#000",
                                          opacity: 0.75,
                                        },
                                      },
                                      "& .img-wrapper-overlay--visible": {
                                        visibility: "visible",
                                        opacity: 1,
                                        position: "relative",
                                        zIndex: 5,
                                      },
                                      "& .card-badges": { zIndex: 5 },
                                      "a&": {
                                        display: "block",
                                        overflow: "hidden",
                                        "&:hover .img-wrapper-overlay": { opacity: 1, visibility: "visible" },
                                      },
                                    }]}>
                                      <Box sx={[{ backgroundImage: 'url(' + image.url_small + ')', height: 500, display: 'flex', justifyContent: 'center', alignItems: 'center', }, { backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" }]}>
                                        <Box component="img" src={image.url} alt="..." loading="lazy" sx={[{ maxHeight: 500, height: 'auto', borderBottomRightRadius: '0px', objectFit: 'fill', }, { borderRadius: '0.65rem', }, { flexShrink: 0, width: "100%" }]}/>
                                      </Box>
                                    </Box>
                                    {/* {!props.shared && !props.public ? image.url === property.mainImage ? (
                              <Chip
                                icon={<Home />}
                                label="Primary Image" sx={[strategyPrimaryBadgeSx, strategyWhiteTextSx]}/>
                            ) : index < property.images.length ? (
                                <Chip
                                  icon={<Image />}
                                  label="Make Primary Image"
                                  onClick={switchMain(index)} sx={strategyAbsBottomLeftSx}/>
                              ) : null : null} */}
                                  </Grid>
                                </Grid>
                              </Card>
                            </div>
                          ),
                        )
                      : null}
                  </Slider>
                </div>

                {hiddenUp ? null : (
                  <Box sx={strategyMargin10Sx}>
                    <Button
                      fullWidth
                     
                      onClick={() => props.toggleCenter('Images')}
                    >
                      <ImageOutlinedIcon style={strategyIconGap5Style} />
                      Manage Images
                    </Button>
                  </Box>
                )}
              </div>
            )}
            {activeTab === 'Map' && (
              <Map
                properties={[property]}
                propertyPage
                streetView={false}
                setView={toggle}
              />
            )}
            {activeTab === 'Street' && (
              <Map
                properties={[property]}
                propertyPage
                streetView={true}
                setView={toggle}
              />
            )}
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 5, xl: 4 }}>
          <CardSummary
            property={property}
            dateCreatedString={dateCreatedString}
            props={props}
            analytics={analytics}
            analysis={analysis}
            currencyFormat={currencyFormat}
            numberFormat={numberFormat}
            strategyCards={strategyCards}
            gorillaSplit={gorillaSplit}
            gorillaFinalFee={gorillaFinalFee}
            loading={loading}
            setLoading={setLoading}
            callPropertyExplorerReport={callPropertyExplorerReport}
            setOpenMemberDisclaimer={props.setOpenMemberDisclaimer}
            setOpenMember={props.setOpenMember}
            toggleLimit={props.toggleLimit}
            toggleCenter={props.toggleCenter}
            userId={props?.session?.me?._id}
          />
          {/* <Card sx={strategyWidthFullRadius0Sx}>
          <CardHeader
            title={<Typography variant="h6" sx={strategyFontSemiboldSx}>Summary</Typography>}
            subheader={<Typography sx={strategyMutedMetaSx}>Added by {property.createdBy ? property.createdBy.profile.firstname + ' ' + property.createdBy.profile.lastname[0] : 'Unknown'} on {dateCreatedString}</Typography>}
            action={!props.public ?
              <Tooltip title="Edit Summary Details">
                <Button onClick={() => props.toggleCenter("Edit")}>
                  <FontAwesomeIcon
                    icon={['far', 'check']}
                  />
                </Button>
              </Tooltip> : null
            }
          />
          <Divider />
          <CardContent>
            <Box sx={strategyPadVert1RemSx}>
              {map(summaryDetails, section => (
                section.name !== 'Status' ? (
                  <Box key={section.name} sx={strategySectionRowSx}>
                    <Box component="span" sx={strategyLabel16BoldSx}>
                      {section.name}:
                    </Box>
                    <Box component="span" sx={strategyFont16Sx}>{section.formatter ? section.formatter.format(section.value) || '-' : section.value || '-'}</Box>
                  </Box>
                ) : (
                    <Box key={section.name} sx={strategySectionRowSx}>
                      <Box component="span" sx={strategyLabel16BoldSx}>
                        {section.name}:
                      </Box>
                      <Box component="span" sx={strategyFont16Sx}>{section.formatter || '-'}</Box>
                    </Box>
                  )
              ))}
              <Box sx={strategySummaryInsetPadSx}>
                <Box sx={strategySectionRowLooseSx}>
                  <Box component="span" sx={strategyLabel16BoldSx}>
                    ARV:
                  </Box>
                  <Box component="span" sx={strategyFont16Sx}>{currencyFormat.format(analysis.afterRepairValue)}</Box>
                </Box>
                <Box sx={strategySectionRowSx}>
                  <Box component="span" sx={strategyLabel16BoldSx}>
                    Rent:
                  </Box>
                  <Box component="span" sx={strategyFont16Sx}>{currencyFormat.format(analysis.rent)}</Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
          {!props.public && (
            <CardActions>
              <Grid container>
                <Grid size={12} justifyContent="flex-end">
                  <ContactAgent property={property} propertySummary={true} />
                </Grid>
              </Grid>
            </CardActions>
          )}
        </Card> */}
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{ width: '100%', p: '25px', pt: 0 }}
      >
        <Grid size={12} sx={{ width: '100%' }}>
          <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
            {map(
              filter(strategyCards, (card) =>
                !isEmpty(property.strategies)
                  ? includes(property.strategies, card.name)
                  : find(
                      get(props.preferences, 'general.strategies'),
                      { value: card.name, active: true },
                    ),
              ),
              (section, index) => (
                <Grid key={section.page || section.name} size={{ xs: 12, md: 6, xl: 4 }} sx={{ width: '100%' }}>
                  <Card variant="outlined" sx={{ borderRadius: 0, width: '100%' }}>
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                          sx={strategyFontSemiboldSx}
                        >
                          {getStrategyName(
                            section.label,
                            activeOrg?.member,
                          )}
                        </Typography>
                      }
                      subheader={
                        <>
                          {`According to your inputs, a ${getStrategyName(
                            section.label,
                            activeOrg?.member,
                          )} strategy meets `}
                          <Box
                            component="b"
                            sx={{
                              color:
                                section.percentage >= 0.66
                                  ? '#1bc943'
                                  : section.percentage >= 0.33
                                    ? '#f4772e'
                                    : '#f83245',
                            }}
                          >
                            {gaugeFormat.format(section.percentage)}
                          </Box>
                          {' of your targets'}
                        </>
                      }
                      // action={
                      //   <IconButton>
                      //     <FontAwesomeIcon
                      //       icon={['far', 'chess-rook']}
                      //     //
                      //     />
                      //   </IconButton>
                      // }
                    />
                    <CardContent>
                      <Grid container direction="column">
                        <Grid size={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              flexGrow: 1,
                              justifyContent: 'center',
                              width: '100%',
                            }}
                          >
                            {/* <div /> */}
                            <Box sx={{ mx: 'auto' }}>
                              <GaugeChart
                                hideText
                                id={'chartsGauges' + index}
                                nrOfLevels={6}
                                colors={[
                                  '#f83245',
                                  '#f4772e',
                                  '#1bc943',
                                ]}
                                arcWidth={0.3}
                                percent={section.percentage}
                              />
                            </Box>
                          </Box>
                        </Grid>
                        <Grid size={12} data-hf-grid="keep">
                          <Card sx={strategyCardGraySx}>
                            <CardContent sx={strategyPad10Sx}>
                              <Grid
                                container
                                direction="row"
                                spacing={0}
                                wrap="nowrap" sx={[strategyPad0Sx, {
                                  justifyContent: 'space-around',
                                  alignItems: 'center',
                                }]}>
                                {map(section.analytics, (field) => (
                                  <Grid key={field.label} data-hf-grid="keep">
                                    <Grid
                                      container
                                      direction="column"
                                      sx={{
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Grid size={12}>
                                        <Typography sx={strategyNowrap13Sx}>
                                          {field.label}
                                        </Typography>
                                      </Grid>
                                      <Grid size={12}>
                                        <Grid
                                          container
                                          direction="row"
                                          wrap="nowrap"
                                          sx={{
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                          }}
                                        >
                                          {!isUndefined(
                                            field.arrow,
                                          ) && (
                                            <Grid data-hf-grid="keep">
                                              {field.arrow ? (
                                                field.arrow === 2 ? (
                                                  <Avatar sx={strategyTinyAvatarSx}>
                                                    <ArrowUpwardIcon sx={strategyWhiteSmallIconSx}/>
                                                  </Avatar>
                                                ) : (
                                                  <Avatar sx={strategyTinyAvatarGreenSx}>
                                                    <ArrowUpwardIcon sx={strategyWhiteSmallIconSx}/>
                                                  </Avatar>
                                                )
                                              ) : (
                                                <Avatar sx={strategyTinyAvatarRedSx}>
                                                  <ArrowDownwardIcon sx={strategyWhiteSmallIconSx}/>
                                                </Avatar>
                                              )}
                                            </Grid>
                                          )}
                                          <Grid sx={strategyMarginLeft5Sx}>
                                            <Typography sx={strategyLabel12BoldSx}>
                                              {/* {field.value !== '-' ? field.type === 'currency' ? currencyFormat.format(field.value) : percentageFormat.format(field.value) : field.value} */}
                                              {field.value !== '-'
                                                ? field.type ===
                                                  'currency'
                                                  ? currencyFormat.format(
                                                      field.value,
                                                    )
                                                  : field.type ===
                                                        'percentage' &&
                                                      field.value >
                                                        1000
                                                    ? '+1000%'
                                                    : percentageFormat.format(
                                                        field.value,
                                                      )
                                                : field.value}
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                ))}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth={true}
                       
                        onClick={() =>
                          props.toggleCenter(section.page)
                        } sx={{ marginBottom: '10px' }}>
                        <Typography sx={strategyFontSemiboldSx}>
                          View Full Analysis
                        </Typography>
                        <FontAwesomeIcon icon={['fas', 'arrow-right']} sx={{ fontSize: "1.15rem !important" }} style={strategyIconMl10Style}/>
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ),
            )}
            {/* EDIT STRATEGIES DIDN'T LOOK GREAT SO IT IS COMMENTED OUT. IT IS ALREADY IN THE LEFT NAV */}
            {/* <EditStrategies property={property} selected={[property._id]} editProperty /> */}
          </Grid>
        </Grid>
        <Grid size={12} sx={strategyFullWidthSx}>
          <Card>
            <CardContent>
              <Grid container direction="column">
                <Grid>
                  <Typography
                    variant="h6" sx={strategySemiboldMb10Sx}>
                    Description
                  </Typography>
                </Grid>
                <Grid>
                  {property.description ? (
                    <FormattedDescription
                      text={property.description}
                    />
                  ) : (
                    <Typography>-</Typography>
                  )}
                </Grid>
              </Grid>
              <Divider sx={{ marginTop: '15px', marginBottom: '15px' }}/>
              <Grid container direction="column">
                <Grid>
                  <Typography
                    variant="h6" sx={strategySemiboldMb10Sx}>
                    Property Details
                  </Typography>
                </Grid>
                <Grid>
                  <Grid
                    container
                    direction="row"
                    sx={{
                      justifyContent: 'space-between',
                    }}
                  >
                    {map(propertyDetails1, (section) => (
                      <Grid key={section.name} size={3} sx={strategyMarginBottom10Sx}>
                        <Typography>
                          <span sx={strategyFontSemiboldSx}>
                            {section.name}:
                          </span>{' '}
                          {section.formatter
                            ? section.formatter.format(section.value)
                            : section.value}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                {!props.public &&
                  (!isEmpty(sessionOrg?.affiliate) ||
                    sessionOrg?.member) && (
                    <Grid>
                      <Grid container direction="row">
                        {map(propertyDetails2, (section) => (
                          <Grid key={section.name} size={3} sx={strategyMarginBottom10Sx}>
                            <Typography>
                              <span sx={strategyFontSemiboldSx}>
                                {section.name}:
                              </span>{' '}
                              {section.formatter
                                ? section.formatter.format(
                                    section.value,
                                  )
                                : section.value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                {sessionOrg?.isRealeflow &&
                  sessionOrg?.member === 'pep' && (
                    <Grid>
                      <Grid container direction="row">
                        {map(propertyDetailsPEP, (section) => (
                          <Grid key={section.name} size={3} sx={strategyMarginBottom10Sx}>
                            <Typography>
                              <span sx={strategyFontSemiboldSx}>
                                {section.name}:
                              </span>{' '}
                              {section.formatter
                                ? section.formatter.format(
                                    section.value,
                                  )
                                : section.value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {!isEmpty(property.skipTraceResults) && (
          <Grid size={12} sx={strategyFullWidthSx}>
            <Card>
              <CardContent>
                <Grid
                  container
                  direction="row" sx={[strategyMarginBottom10Sx, {
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }]}>
                  <Grid>
                    <Typography
                      variant="h6"
                      sx={strategyFontSemiboldSx}
                    >
                      Skip Trace
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container direction="row" spacing={2}>
                  {SkipTraceDisplay(property.skipTraceResults)}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default withSession(CenterPageSummary)
